import { useEffect } from 'react';
import { useLocation } from 'infrastructure/router';
// @mui
import { Box, Stack, Drawer } from '@mui/material';
// hooks
import useResponsive from 'hooks/useResponsive';
// config
import { NAV } from 'consts/ui';
// components
import Logo from 'lib/logo';
import Scrollbar from 'lib/scrollbar';
import { NavSectionVertical } from 'lib/nav-section';
//
import NavDocs, { type NavDocsProps } from './NavDocs';
import NavAccount from './NavAccount';
import useConfig from './useNavConfig';

// ----------------------------------------------------------------------

type Props = NavDocsProps & {
  openNav: boolean;
  onCloseNav: VoidFunction;
};

export default function NavVertical({
  openNav,
  onCloseNav,
  shouldRenderDocumentation
}: Props) {
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const { getConfig } = useConfig();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
        }}
      >
        <Logo brandingImage />

        <NavAccount />
      </Stack>

      <NavSectionVertical data={getConfig()} />

      <Box sx={{ flexGrow: 1 }} />

      <NavDocs
        shouldRenderDocumentation={shouldRenderDocumentation}
      />
    </Scrollbar>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_DASHBOARD },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: NAV.W_DASHBOARD,
              bgcolor: 'transparent',
              borderRightStyle: 'dashed',
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: {
              width: NAV.W_DASHBOARD,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
